import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f5f1ed] to-white">
      <div className="bg-[#3d2817] text-white py-6 shadow-lg">
        <div className="max-w-4xl mx-auto px-4">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-amber-200 hover:text-white text-sm mb-3 transition-colors">
            <ArrowLeft size={16} /> Back to Home
          </button>
          <div className="flex items-center gap-3">
            <Shield size={28} className="text-amber-300" />
            <h1 className="text-2xl font-bold">Privacy Policy</h1>
          </div>
          <p className="text-amber-200 text-sm mt-1">Last updated: July 6, 2026</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="prose prose-stone max-w-none space-y-8 text-sm leading-relaxed text-stone-700">

          <section>
            <h2 className="text-lg font-bold text-[#3d2817] border-b border-stone-200 pb-2">1. Legal Information</h2>
            <p>This website and mobile application are published by:</p>
            <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 mt-2 text-xs leading-relaxed">
              <p className="font-semibold text-stone-800">La Taverne - Restaurant & Grillades</p>
              <p>SAS (Simplified Joint-Stock Company) with a share capital of EUR 7,622.45</p>
              <p>Registered office: 26 Rue Louis Mole, 97300 Cayenne, French Guiana, France</p>
              <p>SIRET: 403 707 177 00016 | SIREN: 403 707 177</p>
              <p>EU VAT Number: FR10403707177</p>
              <p>Business Activity Code (APE): 5610A</p>
              <p>Phone: +594 694 23 41 54 / +594 594 29 04 66</p>
              <p>Email: staff@latavernecay.fr</p>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-bold text-[#3d2817] border-b border-stone-200 pb-2">2. Data We Collect</h2>
            <p>When you use our website and mobile application, we may collect the following personal data:</p>
            <h3 className="font-semibold text-stone-800 mt-4">2.1 When making a reservation</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li>First and last name</li>
              <li>Phone number</li>
              <li>Email address</li>
              <li>Number of guests</li>
              <li>Reservation date and time</li>
              <li>Special requests or messages (optional)</li>
            </ul>
            <h3 className="font-semibold text-stone-800 mt-4">2.2 When placing an order on-site (via QR code)</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li>First name or name (optional, to identify the order)</li>
              <li>Table number</li>
              <li>Order details (dishes, drinks, quantities)</li>
            </ul>
            <h3 className="font-semibold text-stone-800 mt-4">2.3 When leaving a review</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li>Name or nickname</li>
              <li>Rating and comment</li>
            </ul>
            <h3 className="font-semibold text-stone-800 mt-4">2.4 Technical data</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Operating system</li>
              <li>Pages visited and duration of visit</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-[#3d2817] border-b border-stone-200 pb-2">3. Purpose of Data Processing</h2>
            <p>The personal data we collect is used for the following purposes:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Reservation management:</strong> processing, confirmation, alternative time proposals, and email reminders</li>
              <li><strong>Order management:</strong> handling, preparation, and tracking of in-restaurant orders</li>
              <li><strong>Communication:</strong> sending reservation confirmations and invoices by email</li>
              <li><strong>Customer records:</strong> maintaining a reservation history to improve service quality</li>
              <li><strong>Service improvement:</strong> analyzing customer reviews and usage statistics</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-[#3d2817] border-b border-stone-200 pb-2">4. Legal Basis for Processing</h2>
            <p>The processing of your personal data is based on:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Performance of a contract:</strong> when you make a reservation or place an order (Article 6(1)(b) GDPR)</li>
              <li><strong>Legitimate interest:</strong> for improving our services and managing our customer base (Article 6(1)(f) GDPR)</li>
              <li><strong>Consent:</strong> for submitting customer reviews (Article 6(1)(a) GDPR)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-[#3d2817] border-b border-stone-200 pb-2">5. Data Retention Period</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Reservation data:</strong> retained for 3 years from the last interaction</li>
              <li><strong>Order data:</strong> retained for the legally required accounting document retention period (10 years)</li>
              <li><strong>Customer records:</strong> retained for 3 years from the last reservation</li>
              <li><strong>Customer reviews:</strong> retained as long as they remain relevant and published on the website</li>
              <li><strong>Technical data:</strong> retained for a maximum of 12 months</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-[#3d2817] border-b border-stone-200 pb-2">6. Data Recipients</h2>
            <p>Your personal data is accessible only to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Authorized personnel of La Taverne (dining room staff, kitchen staff, management)</li>
              <li>Our web hosting provider (Hostinger) for technical hosting purposes</li>
            </ul>
            <p className="mt-2"><strong>We never sell, rent, or share your personal data with third parties for commercial purposes.</strong></p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-[#3d2817] border-b border-stone-200 pb-2">7. Data Transfers Outside the EU</h2>
            <p>Your data is hosted on servers located in Europe. French Guiana is a French overseas department and region, and the General Data Protection Regulation (GDPR) fully applies. No data transfers to third countries are made.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-[#3d2817] border-b border-stone-200 pb-2">8. Your Rights</h2>
            <p>In accordance with the GDPR and the French Data Protection Act, you have the following rights:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Right of access:</strong> obtain confirmation that your data is being processed and receive a copy</li>
              <li><strong>Right to rectification:</strong> have inaccurate or incomplete data corrected</li>
              <li><strong>Right to erasure:</strong> request the deletion of your personal data</li>
              <li><strong>Right to restriction:</strong> request the restriction of processing in certain cases</li>
              <li><strong>Right to data portability:</strong> receive your data in a structured, machine-readable format</li>
              <li><strong>Right to object:</strong> object to the processing of your data on legitimate grounds</li>
            </ul>
            <p className="mt-3">To exercise your rights, please contact us:</p>
            <div className="bg-stone-50 border border-stone-200 rounded-lg p-3 mt-2 text-xs">
              <p>Email: <strong>staff@latavernecay.fr</strong></p>
              <p>Mail: La Taverne, 26 Rue Louis Mole, 97300 Cayenne, French Guiana, France</p>
              <p>Phone: +594 694 23 41 54</p>
            </div>
            <p className="mt-2">We commit to responding to your request within 30 days.</p>
            <p className="mt-2">You also have the right to lodge a complaint with the CNIL (French National Commission on Informatics and Liberty): <a href="https://www.cnil.fr" className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">www.cnil.fr</a></p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-[#3d2817] border-b border-stone-200 pb-2">9. Data Security</h2>
            <p>We implement appropriate technical and organizational measures to protect your personal data:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>SSL/TLS encryption for all communications</li>
              <li>Restricted data access through authentication and role-based authorization</li>
              <li>Regular data backups</li>
              <li>Hashed passwords (bcrypt)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-[#3d2817] border-b border-stone-200 pb-2">10. Cookies</h2>
            <p>Our website only uses strictly necessary technical cookies for the operation of the service (authentication session, display preferences). No advertising or third-party tracking cookies are used.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-[#3d2817] border-b border-stone-200 pb-2">11. Mobile Application</h2>
            <p>The La Taverne mobile application collects the same data as the website, under the same conditions and for the same purposes described above. The application may also:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Use the camera to scan table QR codes (no images are stored)</li>
              <li>Store data locally on your device for offline functionality (this data is synchronized with the server once a connection is re-established)</li>
              <li>Send push notifications (only if you authorize them)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-[#3d2817] border-b border-stone-200 pb-2">12. Changes to This Policy</h2>
            <p>We reserve the right to modify this privacy policy at any time. Any changes will be published on this page with the updated date. We encourage you to review it regularly.</p>
          </section>

          <section className="bg-[#3d2817]/5 border border-[#3d2817]/10 rounded-lg p-4">
            <p className="text-xs text-stone-500 text-center">
              La Taverne SAS - 26 Rue Louis Mole, 97300 Cayenne, French Guiana, France<br />
              SIRET: 403 707 177 00016 | EU VAT: FR10403707177<br />
              Privacy policy effective as of July 6, 2026
            </p>
          </section>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
